import { useEffect, useState } from 'react';
import TileLayer from 'ol/layer/Tile';
import TileWMS from 'ol/source/TileWMS';
import { unByKey } from 'ol/Observable';
import type { Coordinate } from 'ol/coordinate';
import Point from 'ol/geom/Point';
import { useMapContext } from '../../map/MapContext';
import { PhotoDetailPanel } from '../PhotoDetailPanel/PhotoDetailPanel';
import type { PhotoProperties } from '../../map/photoLayer';
import './identify-popup.css';

interface FeatureInfo {
  layerTitle: string;
  properties: Record<string, unknown>;
}

interface PhotoHit {
  properties: PhotoProperties;
  coordinate: Coordinate;
}

/**
 * Click-to-identify: on a plain map click (not while drawing/measuring),
 * issues a WMS GetFeatureInfo against every visible, queryable layer under
 * the cursor and shows the first hit's attributes in a small popup.
 * Validate against `abrar:survey_points` per the plan's verification step.
 */
export function IdentifyPopup() {
  const { map, layers, activeMeasureTool, selectMeasurement } = useMapContext();
  const [info, setInfo] = useState<FeatureInfo | null>(null);
  const [pixel, setPixel] = useState<[number, number] | null>(null);
  const [loading, setLoading] = useState(false);
  const [photoHit, setPhotoHit] = useState<PhotoHit | null>(null);

  useEffect(() => {
    const key = map.on('singleclick', async (evt) => {
      // Skip identify entirely while a measure tool is armed — the Draw
      // interaction on the same map owns clicks for placing vertices, and
      // this would otherwise pop an unrelated identify/detail panel mid-draw.
      if (activeMeasureTool) return;
      // Also skip if the click somehow originated from toolbar chrome
      // itself (defensive — normally toolbar buttons stop their own
      // propagation, but the map's 'singleclick' is a synthetic OL event
      // derived from the browser click, so belt-and-braces here).
      const target = evt.originalEvent?.target as HTMLElement | undefined;
      if (target?.closest?.('.bottom-toolbar')) return;

      // Measurements and Field Photo pins are ordinary vector layers, not
      // WMS rasters — hit-test them directly (same click, same "click to
      // open" pattern the WMS GetFeatureInfo loop below uses) before
      // falling through to GetFeatureInfo, and show their own dedicated
      // panel instead of the generic attribute table.
      const measurementFeature = map.forEachFeatureAtPixel(evt.pixel, (feature) => feature, {
        layerFilter: (l) => l.get('kind') === 'measurement',
        hitTolerance: 6,
      });
      if (measurementFeature) {
        setInfo(null);
        setPhotoHit(null);
        selectMeasurement(String(measurementFeature.getId()));
        return;
      }

      const photoFeature = map.forEachFeatureAtPixel(evt.pixel, (feature) => feature, {
        layerFilter: (l) => l.get('kind') === 'photo',
        hitTolerance: 6,
      });
      if (photoFeature) {
        const geom = photoFeature.getGeometry();
        if (geom instanceof Point) {
          const { geometry: _geometry, ...properties } = photoFeature.getProperties();
          setInfo(null);
          selectMeasurement(null);
          setPhotoHit({
            properties: properties as PhotoProperties,
            coordinate: geom.getCoordinates(),
          });
          return;
        }
      }
      setPhotoHit(null);
      selectMeasurement(null);

      const view = map.getView();
      const viewResolution = view.getResolution();
      const projection = view.getProjection();
      if (!viewResolution) return;

      const queryable = layers.filter(
        (l) => l.kind !== 'basemap' && l.layer.getVisible() && l.layer instanceof TileLayer,
      );

      for (const entry of queryable) {
        const source = (entry.layer as TileLayer).getSource();
        if (!(source instanceof TileWMS)) continue;
        const url = source.getFeatureInfoUrl(evt.coordinate, viewResolution, projection, {
          INFO_FORMAT: 'application/json',
          FEATURE_COUNT: 1,
        });
        if (!url) continue;
        setLoading(true);
        try {
          const res = await fetch(url);
          if (!res.ok) continue;
          const data = await res.json();
          const feature = data?.features?.[0];
          if (feature) {
            setInfo({ layerTitle: entry.title, properties: feature.properties ?? {} });
            setPixel(evt.pixel as [number, number]);
            setLoading(false);
            return;
          }
        } catch {
          // Unreachable GeoServer during local dev — don't crash, just skip.
        }
      }
      setLoading(false);
      setInfo(null);
    });

    return () => unByKey(key);
  }, [map, layers, activeMeasureTool, selectMeasurement]);

  if (photoHit) {
    return (
      <PhotoDetailPanel
        properties={photoHit.properties}
        coordinate={photoHit.coordinate}
        onClose={() => setPhotoHit(null)}
      />
    );
  }

  if (!info && !loading) return null;

  return (
    <div
      className="identify-popup"
      style={pixel ? { left: pixel[0] + 12, top: pixel[1] + 12 } : undefined}
    >
      <div className="identify-popup-header">
        <span>{loading ? 'Identifying…' : info?.layerTitle}</span>
        <button type="button" onClick={() => setInfo(null)} aria-label="Close">
          ×
        </button>
      </div>
      {info && (
        <table className="identify-popup-table">
          <tbody>
            {Object.entries(info.properties).map(([k, v]) => (
              <tr key={k}>
                <th>{k}</th>
                <td>{String(v)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
